import { FormEvent, useMemo, useRef, useState } from "react";
import { Bot, Loader2, Mic, MicOff, SendHorizonal, Trash2, Volume2 } from "lucide-react";

import { AgentApprovalBar } from "@/components/agent/AgentApprovalBar";
import { AgentMessage } from "@/components/agent/AgentMessage";
import { AgentPlanView } from "@/components/agent/AgentPlanView";
import { AgentQuickActions } from "@/components/agent/AgentQuickActions";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAgent } from "@/hooks/useAgent";

type SpeechRecognitionAlternativeLike = {
  transcript: string;
};

type SpeechRecognitionResultLike = {
  0: SpeechRecognitionAlternativeLike;
  isFinal: boolean;
  length: number;
};

type SpeechRecognitionResultListLike = {
  length: number;
  [index: number]: SpeechRecognitionResultLike;
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: SpeechRecognitionResultListLike;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  start: () => void;
  stop: () => void;
};

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

export function AgentPanel() {
  const [message, setMessage] = useState("");
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const {
    panelOpen,
    closePanel,
    transcript,
    lastResponse,
    isBusy,
    sendMessage,
    approvePlan,
    cancelSession,
    clearConversation,
    runQuickAction,
  } = useAgent();

  const waitingForApproval = lastResponse?.status === "awaiting_approval";

  const startListening = () => {
    const speechWindow = window as Window & {
      SpeechRecognition?: SpeechRecognitionConstructor;
      webkitSpeechRecognition?: SpeechRecognitionConstructor;
    };

    const SpeechRecognitionCtor =
      speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;

    if (!SpeechRecognitionCtor) {
      alert("Voice input not supported in your browser.");
      return;
    }

    const recognition = new SpeechRecognitionCtor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event: SpeechRecognitionEventLike) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcriptChunk = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          setMessage((prev) => (prev ? `${prev} ${transcriptChunk}` : transcriptChunk));
        }
      }
    };

    recognition.start();
    recognitionRef.current = recognition;
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const submitMessage = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || isBusy) {
      return;
    }

    sendMessage(trimmed);
    setMessage("");
  };

  const helperText = useMemo(() => {
    if (!lastResponse) {
      return "Ask seaDrive AI to organize files, explain OCR text, or suggest a cleanup plan.";
    }
    if (lastResponse.status === "awaiting_approval") {
      return "This plan includes file changes, so nothing will run until you approve it.";
    }
    if (lastResponse.status === "executing") {
      return "The approved steps are running now. Progress will refresh automatically.";
    }
    return "You can continue the same AI session or start a fresh one.";
  }, [lastResponse]);

  return (
    <Sheet open={panelOpen} onOpenChange={(open) => (open ? undefined : closePanel())}>
      <SheetContent className="flex w-full flex-col gap-0 border-l border-border/80 bg-gradient-to-b from-background-primary via-background-primary to-background-secondary/20 p-0 shadow-2xl sm:max-w-[560px]">
        <SheetHeader className="border-b border-border/70 bg-gradient-to-r from-background-elevated/40 to-accent-subtle/10 px-5 py-6 backdrop-blur-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-accent to-accent/60 text-white shadow-lg">
                  <Bot className="h-5 w-5" />
                </div>
                <div>
                  <SheetTitle className="text-lg font-bold">seaDrive AI</SheetTitle>
                  <SheetDescription className="mt-1 text-xs">{helperText}</SheetDescription>
                </div>
              </div>
            </div>
            <Button size="sm" type="button" variant="ghost" onClick={clearConversation} className="hover:bg-background-secondary/60">
              <Trash2 className="h-4 w-4" />
              Reset
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {transcript.length === 0 ? (
            <div className="rounded-[28px] border border-dashed border-border/70 bg-background-secondary/40 p-5">
              <p className="text-sm font-semibold text-text-primary">Start with something practical</p>
              <p className="mt-1 text-sm leading-6 text-text-secondary">
                Try "find all receipts from March", "summarize this folder", or "suggest a safe cleanup plan".
              </p>
            </div>
          ) : null}

          {transcript.map((entry) => (
            <AgentMessage
              key={entry.id}
              content={entry.content}
              createdAt={entry.createdAt}
              role={entry.role}
            />
          ))}

          {lastResponse ? <AgentPlanView response={lastResponse} /> : null}

          {waitingForApproval ? (
            <AgentApprovalBar busy={isBusy} onApprove={approvePlan} onCancel={cancelSession} />
          ) : null}
        </div>

        <div className="border-t border-border/70 bg-background-secondary/30 px-5 py-5 backdrop-blur-sm">
          <AgentQuickActions
            busy={isBusy}
            onCleanup={() => runQuickAction("cleanup")}
            onFind={() => runQuickAction("find", { message: "Find important files I should look at today." })}
            onOrganize={() => runQuickAction("organize")}
            onSummarize={() => runQuickAction("summarize")}
          />

          <form className="mt-5 space-y-3" onSubmit={submitMessage}>
            <div className="relative">
              <textarea
                className="min-h-[100px] w-full resize-none rounded-2xl border border-border/60 bg-background-elevated/90 px-4 py-3 text-sm text-text-primary outline-none transition-all placeholder:text-text-tertiary focus:border-accent/50 focus:ring-2 focus:ring-accent/10"
                placeholder="Ask seaDrive AI to organize files, summarize a folder, or explain a document..."
                value={message}
                onChange={(event) => setMessage(event.target.value)}
              />
              <button
                type="button"
                onClick={isListening ? stopListening : startListening}
                disabled={isBusy}
                className={`absolute bottom-3 right-3 rounded-lg p-2 transition-all ${
                  isListening
                    ? "bg-danger/20 text-danger hover:bg-danger/30"
                    : "bg-background-secondary/80 text-text-tertiary hover:bg-accent/20 hover:text-accent"
                } disabled:opacity-50`}
                title={isListening ? "Stop listening" : "Start voice input"}
              >
                {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
            </div>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-text-tertiary">Destructive actions always stay approval-based.</p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  type="button"
                  variant="outline"
                  disabled={isBusy}
                  className="gap-2"
                  onClick={() => {
                    const utterance = new SpeechSynthesisUtterance("seaDrive AI is ready to help");
                    speechSynthesis.speak(utterance);
                  }}
                >
                  <Volume2 className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Test</span>
                </Button>
                <Button disabled={isBusy || message.trim().length === 0} type="submit" className="gap-2">
                  {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizonal className="h-4 w-4" />}
                  <span className="hidden sm:inline">Send</span>
                </Button>
              </div>
            </div>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
